import React, { createRef, useEffect, useState } from 'react'
import { Button } from '../Button'
import { Input } from '../Input'
import { SectionContainer } from '../SectionContainer'
import { Text1 } from '../Text1'
import { Textarea } from '../Textarea'
import styles from './EntityInfoSection.module.scss'
import cl from 'classnames'
import {
  EntityInfoPropertyType,
  EntityInfoSectionProps
} from './EntityInfoSection.types'
import { HorizontalLine } from '../HorizontalLine'
import { GalleryDropZone, GalleryDropZoneRef } from '../GalleryDropZone'
import { IconEdit } from 'assets'
import { inject } from 'mobx-react'
import { GENERAL_STORE } from 'store'
import { LabeledLine } from '../LabeledLine'
import { TagsEditor } from '../TagsEditor'
import { EntityInfoSectionLayout } from '../EntityInfoSectionLayout'
import { EntityInfoNameLine } from '../EntityInfoNameLine'

const EntityInfoSectionComponent = (props: EntityInfoSectionProps) => {
  const {
    mainImage,
    name,
    setName,
    ratingProps,
    saveChanges,
    discardChanges,
    propertyFields,
    textareaField,
    images,
    imagesRef: imagesRefProp,
    editMode: editModeProp,
    onEditModeOn,
    onEditModeOff,
    children,
    sectionContainerProps,
    dropZoneOptions,
    [GENERAL_STORE]: { setLoadingOn, showError }
  } = props

  const [editMode, setEditMode] = useState(Boolean(editModeProp))

  useEffect(() => {
    if (!onEditModeOn && !onEditModeOff) return

    if (editMode && onEditModeOn) onEditModeOn()
    if (!editMode && onEditModeOff) onEditModeOff()
  }, [editMode, onEditModeOn, onEditModeOff])

  const imagesRef: GalleryDropZoneRef = createRef()

  const handleSaveChanges = () => {
    if (!saveChanges) return
    setLoadingOn()
    imagesRef.current
      .handleImages()
      .then(() => {
        saveChanges(imagesRef.current.getImages())
        setEditMode(false)
      })
      .catch(err => showError(err.message))
  }

  const handleDiscardChanges = () => {
    setEditMode(false)
    discardChanges && discardChanges()
  }

  return (
    <SectionContainer {...sectionContainerProps}>
      <EntityInfoNameLine
        entityName={name}
        imageProps={{
          src: mainImage,
          alt: name
        }}
        editMode={editMode}
        nameInputProps={{
          value: name,
          onChange: setName
        }}
        onDiscardChangesClick={handleDiscardChanges}
        onSaveChangesClick={handleSaveChanges}
        ratingProps={ratingProps}
      />

      {children || (
        <EntityInfoSectionLayout
          leftContainerNode={propertyFields.map((property, index) => {
            const isFirstContainer = index === 0
            const isTypeTags = property.type === EntityInfoPropertyType.TAGS

            return (
              <LabeledLine
                key={index}
                preset={isTypeTags ? 'tags' : undefined}
                label={property.label}
                className={cl({
                  [styles.firstFlexContainer]: isFirstContainer
                })}
              >
                {isTypeTags ? (
                  <TagsEditor
                    editMode={editMode}
                    tags={property.value}
                    onChange={property.onChange}
                  />
                ) : editMode && !!property.onChange ? (
                  <Input
                    placeholder={property.label}
                    value={property.value}
                    onChange={property.onChange}
                  />
                ) : (
                  <Text1 overflowHidden={false} >{property.value}</Text1>
                )}
              </LabeledLine>
            )
          })}
          rightContainerNode={
            <LabeledLine
              label={textareaField.label}
              style={{ width: '100%', alignItems: 'stretch', marginTop: 0 }}
              alignItems='flexStart'
            >
              {editMode && !!textareaField.onChange ? (
                <Textarea
                  className={styles.textarea}
                  value={textareaField.value}
                  onChange={textareaField.onChange}
                />
              ) : (
                <Text1 className={cl(styles.description, styles.scrollable)} overflowHidden={false}>
                  {textareaField.value}
                </Text1>
              )}
            </LabeledLine>
          }
        />
      )}

      <HorizontalLine />

      <Text1 bold style={{ marginTop: 14 }}>
        Images
      </Text1>

      {editMode ? (
        <div className={styles.imageEdit}>
          <GalleryDropZone
            ref={imagesRefProp || imagesRef}
            initialImages={images || []}
            dropZoneOptions={dropZoneOptions}
          />
        </div>
      ) : (
        <div className={styles.imagesContainer}>
          <GalleryDropZone
            ref={imagesRefProp || imagesRef}
            initialImages={images || []}
            dropZoneOptions={dropZoneOptions}
          />
        </div>
      )}

      {!editMode && (
        <Button
          preset='icon'
          className={styles.editButton}
          onClick={() => setEditMode(true)}
        >
          <IconEdit />
        </Button>
      )}
    </SectionContainer>
  )
}

const EntityInfoSection = inject(GENERAL_STORE)(EntityInfoSectionComponent)
export { EntityInfoSection }
